import { Component, Inject, OnInit } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { NestedTreeControl } from "@angular/cdk/tree";
import { MatTreeNestedDataSource } from "@angular/material/tree";
import { PrismService } from "../../services/prism.service";
import { CommonService } from "../../services/common.service";
import { FormControl } from "@angular/forms";
import textConfiguration from "src/assets/static-text-configuration.json";

interface DialogData {
  projectId?: string;
  portfolioId?: string;
  subportfolioId?: string;
  programId?: string;
  vendorId?: string;
  vendorName?: string;
  projectName?: string;
  portfolioName?: string;
  subportfolioName?: string;
  programName?: string;
  tagIds?: string[];
  id: string;
  mapped: number;
  searchTags: string;
  searchType: string;
  searchCategory: string;
  isBreadcrumb: boolean;
}

interface TreeNode {
  name: string;
  type: "application" | "infrastructure" | "service" | "directService";
  data: any;
  children?: TreeNode[];
  level: number;
}

@Component({
  selector: "app-tech-map-dialog-data",
  templateUrl: "./tech-map-dialog-data.component.html",
  styleUrls: ["./tech-map-dialog-data.component.scss"],
})
export class TechMapDialogDataComponent implements OnInit {
  loading = false;
  breadCrumbData: string[] = [];
  treeControl = new NestedTreeControl<TreeNode>((node) => node.children);
  dataSource = new MatTreeNestedDataSource<TreeNode>();
  displayLabels: any = {};
  applicationSearchTerm: string = "";
  searchControl = new FormControl(null);
  rawData: any;
  filteredData: any;
  commonText: any = (textConfiguration as any).common;

  constructor(
    public dialogRef: MatDialogRef<TechMapDialogDataComponent>,
    private readonly prismService: PrismService,
    private readonly commonService: CommonService,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {}

  ngOnInit(): void {
    this.displayLabels = this.commonService.getLabels();
    this.buildBreadCrumb();
    this.loadTreeData();
  }

  /** --- Breadcrumb --- */
  buildBreadCrumb(): void {
    this.breadCrumbData.push(this.displayLabels.all_of_it);
    if (this.data.projectName) {
      this.breadCrumbData.push(this.data.portfolioName ?? "Domain");
      this.breadCrumbData.push(this.data.subportfolioName ?? "Subdomain");
      this.breadCrumbData.push(this.data.programName ?? "Program");
      this.breadCrumbData.push(this.data.projectName);
    } else if (this.data.programName) {
      this.breadCrumbData.push(this.data.portfolioName ?? "Domain");
      this.breadCrumbData.push(this.data.subportfolioName ?? "Subdomain");
      this.breadCrumbData.push(this.data.programName);
    } else if (this.data.subportfolioName) {
      this.breadCrumbData.push(this.data.portfolioName ?? "Domain");
      this.breadCrumbData.push(this.data.subportfolioName);
    } else if (this.data.portfolioName) {
      this.breadCrumbData.push(this.data.portfolioName);
    }
    if (this.data.vendorName) {
      this.breadCrumbData.push("OU/GDP",this.data.vendorName);
    }
  }

  hasChild = (_: number, node: TreeNode) =>
    !!node.children && node.children.length > 0;

  private getQueryParams(): string {
    const params: string[] = [];

    // multi-value capable IDs
    const multiValueKeys = [
      { key: "vendor_id", value: this.data.vendorId },
      { key: "portfolio_id", value: this.data.portfolioId },
      { key: "subportfolio_id", value: this.data.subportfolioId },
      { key: "program_id", value: this.data.programId },
      { key: "project_id", value: this.data.projectId },
      { key: "id", value: this.data.id },
    ];

    multiValueKeys.forEach((item) => {
      if (!item.value) return;

      // allow both single numbers or arrays
      const value = Array.isArray(item.value)
        ? item.value.join(",")
        : item.value;

      params.push(`${item.key}=${value}`);
    });

    params.push(`mapped=1`);

    const searchFilters: string[] = [];

    if (this.data.searchTags) {
      searchFilters.push(`tags:${this.data.searchTags}`);
    }
    if (this.data.searchType) {
      searchFilters.push(`type:${this.data.searchType}`);
    }
    if (this.data.searchCategory) {
      searchFilters.push(`category:${this.data.searchCategory}`);
    }

    if (searchFilters.length > 0) {
      params.push(`search=${searchFilters.join(",")}`);
    }

    return params.length > 0 ? `?${params.join("&")}` : "";
  }

  loadTreeData(): void {
    this.loading = true;
    const queryParams = this.getQueryParams();

    this.prismService.getApplicationMasterTree(queryParams).subscribe({
      next: (res: any) => {
        this.rawData = res;
        const treeData = this.buildTreeData(res);
        this.dataSource.data = treeData;
        this.loading = false;
      },
      error: () => {
        console.error("Error fetching Tech Map Tree Data");
        this.loading = false;
      },
    });
  }

  private buildTreeData(applications: any): TreeNode[] {
    return applications.map((app: any) => ({
      name: app.name,
      type: "application",
      data: app,
      level: 0,
      children: [
        ...(app?.services
          ? app?.services.map((svc: any) => ({
              name: svc.service_name,
              type: "directService",
              data: svc,
              level: 2,
              children: [],
            }))
          : []),

        ...app.infrastructures.map((infra: any) => ({
          name: infra.infrastructure_name,
          type: "infrastructure",
          data: infra,
          level: 1,
          children: infra.services.map((svc: any) => ({
            name: svc.service_name,
            type: "service",
            data: svc,
            level: 2,
            children: [],
          })),
        })),
      ],
    }));
  }

  /** --- UI Helpers --- */
  getNodeIcon(node: TreeNode): string {
    switch (node.type) {
      case "application":
        return "apps";
      case "infrastructure":
        return "dns";
      case "service":
        return "build";
      case "directService":
        return "build";
      default:
        return "folder";
    }
  }

  getNodeClass(node: any): string {
    switch (node.type) {
      case "application":
        return "color-application";
      case "infrastructure":
        return "color-infrastructure";
      case "service":
        return "color-service";
      case "directService":
        return "color-service";
      default:
        return "";
    }
  }

  getColumnTooltip(node: any, col: number): string {
    if (node.type === "application") {
      switch (col) {
        case 1:
          return "System Code";
        case 2:
          return "Provider";
        case 3:
          return "Version";
        case 4:
          return "Primary Business Owner";
      }
    } else if (node.type === "infrastructure") {
      switch (col) {
        case 1:
          return "Usage Type";
        case 2:
          return "Category";
        case 3:
          return "Subcategory";
        case 4:
          return "Environment";
      }
    } else if (node.type === "service" || node.type === "directService") {
      switch (col) {
        case 1:
          return "Service Type";
        case 2:
          return "Category";
        case 3:
          return "Subcategory";
        case 4:
          return "Owner";
      }
    }
    return "";
  }

  getColumnClass(node: any, col: number): string {
    return `${node.type}-col${col}`;
  }

  onApplicationSearch(): void {
    const searchLower = this.applicationSearchTerm.toLowerCase().trim();

    if (!searchLower) {
      this.filteredData = [...this.rawData];
    } else {
      this.filteredData = this.rawData.filter((app: TreeNode) =>
        app.name.toLowerCase().includes(searchLower)
      );
    }
    this.dataSource.data = this.buildTreeData(this.filteredData);
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
